/**
 * Turn an API failure into something an operator can act on.
 *
 * `ApiError` carries the server's error code, optionally with a reference id:
 * `vault_unavailable · reference 01J...`. Screens were rendering that string
 * directly, so a failed read showed the operator `request_failed` and nothing
 * else - no indication of whether the thing is broken, not deployed, or simply
 * not reachable from here.
 *
 * The code is still worth showing, because it is what support and the audit log
 * are keyed on. It just belongs after the sentence, not instead of it.
 */

export interface Failure {
  /** A full sentence, always present. */
  sentence: string;
  /** The server's code, when the message carried one worth repeating. */
  code?: string;
  /** Reference id for correlating with the API's own logs. */
  reference?: string;
}

const SENTENCES: Record<string, string> = {
  request_failed:
    'The request did not reach the management API, or it answered in a way this console could not read.',
  unavailable: 'That part of the control plane is not reachable right now.',
  product_api_unavailable:
    'The product API is not reachable from the management plane, so this data cannot be read.',
  operation_controller_unavailable:
    'The deployment controller is not reachable, so operations cannot be read or started.',
  vault_unavailable: 'The vault is not available, so encrypted values cannot be read or written.',
  recovery_unavailable: 'Recovery tooling is not configured on this host.',
  external_recovery_unavailable:
    'External recovery is not configured yet - its bucket and credential have not been provisioned.',
  alert_delivery_unavailable:
    'The vault is not available, so alert delivery credentials cannot be encrypted yet.',
  security_approval_required: 'This action needs a second approver with the security role.',
  request_validation_failed: 'That submission was not the shape this endpoint accepts.',
  request_rejected: 'The management API refused that request.',
  operation_not_found: 'That operation no longer exists, or it was never recorded.',
  access_required: 'This session is not authenticated. Reload to sign in again.',
  access_invalid: 'This session was rejected. Reload to sign in again.',
};

/** Fallback for an unmapped code, so the operator never just sees an identifier. */
function describe(code: string): string {
  if (code.endsWith('_unavailable')) return 'That dependency is not available right now.';
  if (code.endsWith('_conflict'))
    return 'Something else changed this first. Reload to see the current state, then retry.';
  if (code.endsWith('_not_found')) return 'That record does not exist.';
  if (code.endsWith('_rejected') || code.endsWith('_refused'))
    return 'The management API refused that request.';
  if (code.endsWith('_required')) return 'That request is missing something the API requires.';
  return 'The management API could not complete that request.';
}

export function explainFailure(raw: string | undefined): Failure | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const [first, ...rest] = trimmed.split('·').map((part) => part.trim());
  const head = first ?? '';
  const reference = rest
    .map((part) => part.replace(/^reference\s+/i, ''))
    .find((part) => part.length > 0);

  // A bare identifier is a code; anything with spaces is already prose from a
  // thrown Error and should be shown as-is.
  const isCode = /^[a-z][a-z0-9_]*$/.test(head);
  if (!isCode) {
    return {
      sentence: head.endsWith('.') ? head : `${head}.`,
      ...(reference ? { reference } : {}),
    };
  }
  return {
    sentence: SENTENCES[head] ?? describe(head),
    code: head,
    ...(reference ? { reference } : {}),
  };
}
